/** AT-HOME SPONSORS — who pays for tonight's at-home prizes.
 *
 *  In-bar play is untouched. Each sponsor has a name, a logo, the prizes and
 *  gift cards it gives, an optional venue of ours, and an internal label
 *  (Paying / Not paying / Trial — a label only). Pause or remove in one tap.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pause, Play, Trash2 } from 'lucide-react';
import api from '../../../lib/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';

const API = '/goal-game/admin/sponsors';
const LABELS = ['paying', 'not_paying', 'trial'];

export const GoalSponsorsBlock = ({ organizationId }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [draft, setDraft] = useState({ name: '', cap_number: '', cap_percent: '', label: 'trial' });

    const load = useCallback(async () => {
        const res = await api.get(`${API}?organization_id=${organizationId || ''}`);
        setData(res.data);
    }, [organizationId]);

    useEffect(() => { load(); }, [load]);

    const post = async (path, body) => {
        setBusy(true);
        try {
            const res = await api.post(`${API}${path}`, { organization_id: organizationId, ...body });
            setData(res.data);
        } finally { setBusy(false); }
    };

    if (!data) return <p className="text-sm text-muted-foreground">…</p>;

    return (
        <div className="space-y-4" data-testid="gg-sponsors-block">
            <div className="flex flex-wrap items-center gap-2">
                {[['own_venue', t('goalGame.sponsorModeOwn')], ['we_choose', t('goalGame.sponsorModeChoose')]]
                    .map(([key, label]) => (
                        <Button key={key} size="sm" disabled={busy}
                                variant={data.mode === key ? 'default' : 'outline'}
                                data-testid={`gg-sponsor-mode-${key}`}
                                onClick={() => post('/mode', { mode: key })}>
                            {label}
                        </Button>
                    ))}
            </div>

            <div className="space-y-2">
                {(data.sponsors || []).map((s) => (
                    <div key={s.id} className="rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2.5"
                         data-testid={`gg-sponsor-${s.id}`}>
                        <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-sm font-medium truncate">
                                    {s.name}{s.paused ? ` · ${t('goalGame.sponsorPaused')}` : ''}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    {t(`goalGame.sponsorLabel_${s.label || 'trial'}`)} ·{' '}
                                    {s.cap_number ? `${t('goalGame.sponsorCap')}: ${s.cap_number}`
                                        : s.cap_percent ? `${s.cap_percent}%` : t('goalGame.sponsorNoCap')}
                                </p>
                            </div>
                            <div className="flex shrink-0 items-center gap-1">
                                <Button size="sm" variant="outline" disabled={busy}
                                        data-testid={`gg-sponsor-pause-${s.id}`}
                                        onClick={() => post('', { id: s.id, paused: !s.paused })}>
                                    {s.paused ? <Play size={13} /> : <Pause size={13} />}
                                </Button>
                                <Button size="sm" variant="outline" disabled={busy}
                                        data-testid={`gg-sponsor-remove-${s.id}`}
                                        onClick={() => post('/remove', { id: s.id })}>
                                    <Trash2 size={13} />
                                </Button>
                            </div>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            <Input className="h-9 w-24" type="number" min="0" placeholder={t('goalGame.sponsorCap')}
                                   defaultValue={s.cap_number || ''} data-testid={`gg-sponsor-cap-${s.id}`}
                                   onBlur={(e) => post('', { id: s.id, cap_number: Number(e.target.value) || 0 })} />
                            <Input className="h-9 w-24" type="number" min="0" max="100" placeholder="%"
                                   defaultValue={s.cap_percent || ''} data-testid={`gg-sponsor-pct-${s.id}`}
                                   onBlur={(e) => post('', { id: s.id, cap_percent: Number(e.target.value) || 0 })} />
                            {LABELS.map((l) => (
                                <Button key={l} size="sm" disabled={busy}
                                        variant={(s.label || 'trial') === l ? 'default' : 'outline'}
                                        data-testid={`gg-sponsor-label-${l}-${s.id}`}
                                        onClick={() => post('', { id: s.id, label: l })}>
                                    {t(`goalGame.sponsorLabel_${l}`)}
                                </Button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <Input className="h-9 w-48" value={draft.name} data-testid="gg-sponsor-new-name"
                       placeholder={t('goalGame.sponsorName')}
                       onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                <Input className="h-9 w-24" type="number" min="0" value={draft.cap_number}
                       placeholder={t('goalGame.sponsorCap')} data-testid="gg-sponsor-new-cap"
                       onChange={(e) => setDraft({ ...draft, cap_number: e.target.value })} />
                <Button size="sm" disabled={busy || !draft.name.trim()} data-testid="gg-sponsor-add"
                        onClick={async () => {
                            await post('', { name: draft.name.trim(),
                                             cap_number: Number(draft.cap_number) || 0,
                                             label: draft.label });
                            setDraft({ name: '', cap_number: '', cap_percent: '', label: 'trial' });
                        }}>
                    {t('goalGame.sponsorAdd')}
                </Button>
            </div>

            {/* Tonight, per sponsor: what they actually got. */}
            <div className="overflow-x-auto" data-testid="gg-sponsor-summary">
                <table className="w-full text-xs">
                    <thead className="text-muted-foreground">
                        <tr>
                            <th className="py-1 text-left">{t('goalGame.sponsorColumn')}</th>
                            <th className="py-1 text-right">{t('goalGame.sponsorPlayers')}</th>
                            <th className="py-1 text-right">{t('goalGame.sponsorScratches')}</th>
                            <th className="py-1 text-right">{t('goalGame.sponsorPrizes')}</th>
                            <th className="py-1 text-right">{t('goalGame.sponsorGiftCards')}</th>
                            <th className="py-1 text-right">{t('goalGame.sponsorRedeemed')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(data.summary || []).length === 0 ? (
                            <tr><td colSpan={6} className="py-2 text-muted-foreground">—</td></tr>
                        ) : data.summary.map((r) => (
                            <tr key={r.sponsor_id} data-testid={`gg-sponsor-summary-${r.sponsor_id}`}>
                                <td className="py-1">{r.sponsor_name}</td>
                                <td className="py-1 text-right tabular-nums">{r.players}</td>
                                <td className="py-1 text-right tabular-nums">{r.scratches}</td>
                                <td className="py-1 text-right tabular-nums">{r.prizes}</td>
                                <td className="py-1 text-right tabular-nums">{r.gift_cards}</td>
                                <td className="py-1 text-right tabular-nums">{r.redeemed}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default GoalSponsorsBlock;
